import { gold, green, red } from '@ant-design/colors'
import { ReloadOutlined } from '@ant-design/icons'
import { Button, Tag } from 'antd'
import dayjs from 'dayjs'
import React, { FunctionComponent, useState } from 'react'
import { runTitleCheck, useTitleCheck } from 'services'
import styled from 'styled-components'

import displayMessage from 'utils/displayMessage'
import { themeFontSize } from 'utils/styles'

import * as T from 'types'

import { SubsectionTitle } from '../Styles'

const Wrapper = styled.div`
  ${themeFontSize};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;

  th {
    text-align: left;
    font-weight: 600;
    padding: 2px 4px;
  }

  td {
    padding: 2px 4px;
    vertical-align: top;
  }

  tbody tr:nth-child(odd) {
    background-color: ${props => props.theme.colors.grey};
  }
`

const Muted = styled.div`
  padding: 2px 4px;
  color: ${props => props.theme.colors.grey6};
`

const Warning = styled.div`
  padding: 2px 4px;
  margin-bottom: 8px;
  color: ${red[6]};
  font-weight: 600;
`

const matchTag: Record<T.TitleCheckNameMatch, { color: string; label: string }> = {
  match: { color: green[6], label: 'Match' },
  partial: { color: gold[6], label: 'Partial' },
  no_match: { color: red[6], label: 'No match' },
}

// Smarty returns codes like `not_owner_occupied`
const humanize = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ') : ''

const formatAddress = (address?: T.ITitleCheckAddress) =>
  [address?.street, address?.city, [address?.state, address?.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

interface ITitleCheckProps {
  listingId: string
}

export const TitleCheck: FunctionComponent<ITitleCheckProps> = ({ listingId }) => {
  const { titleCheck, comparison, error, data } = useTitleCheck(listingId)
  const [running, setRunning] = useState(false)

  const handleRun = async () => {
    setRunning(true)
    const [, runError] = await runTitleCheck(listingId)
    setRunning(false)
    if (runError) displayMessage('error', runError.message)
  }

  const renderBody = () => {
    if (error) return <Muted>{error}</Muted>

    if (!data) return <Muted>Loading...</Muted>

    if (!titleCheck) {
      return <Muted>No title check yet. It runs automatically after the seller pays.</Muted>
    }

    if (titleCheck.status === 'error') {
      return <Warning>{`The lookup failed: ${titleCheck.errorMessage || 'unknown error'}`}</Warning>
    }

    if (titleCheck.status === 'not_found') {
      return (
        <Muted>{`No public record found for ${formatAddress(titleCheck.requestedAddress)}.`}</Muted>
      )
    }

    const details: [string, string | undefined][] = [
      ['Owners on record', titleCheck.ownerNames.join('; ')],
      ['Deed owners', titleCheck.deedOwnerNames.join('; ')],
      ['Ownership type', humanize(titleCheck.ownershipType)],
      ['Vesting', humanize(titleCheck.vestingRelationCode)],
      ['Trust', humanize(titleCheck.trustDescription)],
      ['Occupancy', humanize(titleCheck.ownerOccupancyStatus)],
      ['Deed date', titleCheck.deedDate && dayjs(titleCheck.deedDate).format('MM/DD/YYYY')],
      ['Title company', titleCheck.titleCompany],
      ['Matched address', formatAddress(titleCheck.matchedAddress)],
    ]

    return (
      <>
        {comparison?.unitMatched === false
          ? (
            <Warning>
              {`This record is for a different unit: ${formatAddress(
                comparison.recordAddress,
              )}. Check the owner with the county before using it.`}
            </Warning>
          )
          : (
            comparison
          && !comparison.addressMatched && (
              <Warning>
                {`Smarty matched a different address: ${formatAddress(titleCheck.matchedAddress)}`}
              </Warning>
            )
          )}
        <Table>
          <thead>
            <tr>
              <th>Entered by seller</th>
              <th>Public record</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {comparison?.owners.map((owner, index) => (
              <tr key={owner.ownerId || index}>
                <td>{owner.enteredName || '---'}</td>
                <td>{owner.recordName || '---'}</td>
                <td>
                  <Tag color={matchTag[owner.result].color}>{matchTag[owner.result].label}</Tag>
                </td>
              </tr>
            ))}
            {comparison?.unmatchedRecordNames.map(name => (
              <tr key={name}>
                <td>---</td>
                <td>{name}</td>
                <td>
                  <Tag color={gold[6]}>Not entered by seller</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Table>
          <tbody>
            {details
              .filter(([, value]) => value)
              .map(([label, value]) => (
                <tr key={label}>
                  <th>{label}</th>
                  <td>{value}</td>
                </tr>
              ))}
          </tbody>
        </Table>
      </>
    )
  }

  return (
    <Wrapper>
      <SubsectionTitle>
        <div>
          Title Check (Smarty)
          {titleCheck && ` · ${dayjs(titleCheck.createdAt).format('MM/DD/YY h:mm a')}`}
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={running}
          onClick={handleRun}
        >
          Run Title Check
        </Button>
      </SubsectionTitle>
      {renderBody()}
    </Wrapper>
  )
}

export default TitleCheck
